import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Layout } from "@/components/Layout";
import { ProductCard } from "@/components/ProductCard";
import { displayBrand } from "@/lib/product-facts";
import {
  PRODUCTS_QUERY,
  storefrontApiRequest,
  type ShopifyProduct,
} from "@/lib/shopify";


const uutuudetQueryOptions = {
  queryKey: ["products", "uutuudet-landing"] as const,
  queryFn: async () => {
    const res = await storefrontApiRequest(PRODUCTS_QUERY, {
      first: 50,
      query: "tag:uutuus",
    });
    return (res?.data?.products?.edges ?? []) as ShopifyProduct[];
  },
};

export const Route = createFileRoute("/uutuudet")({
  loader: ({ context }) => context.queryClient.ensureQueryData(uutuudetQueryOptions),
  head: () => ({
    meta: [
      { title: "Uutuudet — juuri saapuneet Montessori-tuotteet | Montessori24" },
      {
        name: "description",
        content:
          "Uusimmat Montessori-lelut, kiipeilytelineet, oppimistornit ja lasten kalusteet. Koivuvaneri, ilmainen toimitus Suomessa 3–7 päivässä.",
      },
      { name: "keywords", content: "uutuudet, montessori, montessori-lelut, kiipeilyteline, oppimistorni, pikler, pehmopalikat, lasten kalusteet, suomi" },
      { name: "robots", content: "index,follow,max-image-preview:large,max-snippet:-1" },
      { property: "og:title", content: "Uutuudet — juuri saapuneet Montessori-tuotteet" },
      {
        property: "og:description",
        content:
          "Selaa Montessori24:n uusinta valikoimaa: kiipeilytelineet, oppimistornit, pehmopalikat ja pedagogiset lelut. Ilmainen toimitus Suomeen.",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Montessori24" },
      { property: "og:locale", content: "fi_FI" },
      { property: "og:url", content: "https://montessori24.fi/uutuudet" },
      { property: "og:image", content: "https://montessori24.fi/og-image.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Uutuudet — juuri saapuneet Montessori-tuotteet | Montessori24" },
      { name: "twitter:description", content: "Uusimmat Montessori-lelut, kiipeilytelineet ja oppimistornit. Ilmainen toimitus Suomessa 3–7 päivässä." },
      { name: "twitter:image", content: "https://montessori24.fi/og-image.jpg" },
    ],
    links: [
      { rel: "canonical", href: "https://montessori24.fi/uutuudet" },
      { rel: "alternate", hrefLang: "fi", href: "https://montessori24.fi/uutuudet" },
      { rel: "alternate", hrefLang: "x-default", href: "https://montessori24.fi/uutuudet" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Etusivu", item: "https://montessori24.fi/" },
            { "@type": "ListItem", position: 2, name: "Kauppa", item: "https://montessori24.fi/kauppa" },
            { "@type": "ListItem", position: 3, name: "Uutuudet", item: "https://montessori24.fi/uutuudet" },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Uutuudet — Montessori24",
          description:
            "Montessori24:n uusimmat tuotteet: kiipeilytelineet, oppimistornit, pehmopalikat ja pedagogiset lelut.",
          url: "https://montessori24.fi/uutuudet",
          inLanguage: "fi-FI",
          isPartOf: { "@type": "WebSite", name: "Montessori24", url: "https://montessori24.fi/" },
        }),
      },
    ],
  }),
  component: UutuudetPage,
});

function UutuudetPage() {
  const { data } = useSuspenseQuery(uutuudetQueryOptions);
  const products = (data ?? []).filter((p) => p.node.images.edges[0]?.node.url);

  const itemListJsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Uutuudet — Montessori24",
    numberOfItems: products.length,
    itemListElement: products.map((p, i) => {
      const price = p.node.priceRange.minVariantPrice;
      const img = p.node.images.edges[0]?.node.url;
      return {
        "@type": "ListItem",
        position: i + 1,
        url: `https://montessori24.fi/product/${p.node.handle}`,
        item: {
          "@type": "Product",
          name: p.node.title,
          url: `https://montessori24.fi/product/${p.node.handle}`,
          ...(img ? { image: img } : {}),
          ...(displayBrand(p.node.vendor) ? { brand: { "@type": "Brand", name: displayBrand(p.node.vendor)! } } : {}),
          offers: {
            "@type": "Offer",
            price: price.amount,
            priceCurrency: price.currencyCode,
            availability: "https://schema.org/InStock",
            url: `https://montessori24.fi/product/${p.node.handle}`,
          },
        },
      };
    }),
  };

  return (
    <Layout>
      <script
        type="application/ld+json"
        // Product list JSON-LD is per-render because it depends on the fetched list.
        dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListJsonLd) }}
      />

      {/* Trust ribbon */}
      <div className="border-b border-border bg-cream">
        <div className="container-prose flex flex-wrap items-center justify-center gap-x-6 gap-y-1 py-2 text-center text-xs text-muted-foreground">
          <span>✓ Ilmainen toimitus</span>
          <span className="hidden sm:inline">·</span>
          <span>✓ Toimitus 3–7 päivää</span>
          <span className="hidden sm:inline">·</span>
          <span>✓ 30 päivän palautusetu</span>
        </div>
      </div>

      {/* Hero */}
      <section className="border-b border-border bg-sand/30">
        <div className="container-prose py-16 md:py-24">
          <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-clay">
            Juuri saapuneet
          </p>
          <h1 className="mt-3 font-display text-4xl italic leading-[0.95] text-foreground text-balance md:text-6xl">
            Uutuudet
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
            Montessori24:n tuoreimmat lisäykset — kiipeilytelineet,
            oppimistornit, pehmopalikat ja pedagogiset lelut, jotka tukevat lapsen
            motoriikkaa ja itsenäisyyttä. Kaikkiin uutuuksiin ilmainen toimitus
            Suomessa 3–7 arkipäivässä ja 30 päivän palautusetu.
          </p>
        </div>
      </section>

      {/* Grid */}
      <section className="container-prose py-14 md:py-20">
        <div className="flex items-end justify-between gap-4">
          <h2 className="text-2xl text-foreground md:text-4xl">
            Kaikki uutuudet
            {products.length > 0 && (
              <span className="ml-3 text-base font-normal text-muted-foreground md:text-lg">
                ({products.length})
              </span>
            )}
          </h2>
        </div>

        {products.length > 0 ? (
          <div className="mt-8 grid grid-cols-2 gap-4 md:mt-12 md:grid-cols-3 md:gap-6 lg:grid-cols-4">
            {products.map((p, i) => (
              <ProductCard
                key={p.node.id}
                product={p}
                railName="uutuudet"
                railPosition={i}
                sizes="(min-width: 1024px) 22vw, (min-width: 640px) 33vw, 45vw"
              />
            ))}
          </div>
        ) : (
          <div className="mt-12 rounded-xl border border-dashed border-border bg-cream py-16 text-center">
            <p className="text-foreground">Uutuudet julkaistaan pian.</p>
          </div>
        )}
      </section>

      {/* Copy block for SEO depth */}
      <section className="border-t border-border bg-cream">
        <div className="container-prose py-14 md:py-20">
          <div className="mx-auto max-w-3xl space-y-5 text-base leading-relaxed text-foreground/85 md:text-lg">
            <h2 className="text-3xl text-foreground md:text-4xl">
              Uusimmat Montessori-tuotteet lapselle
            </h2>
            <p>
              Täältä löydät Montessori24:n viimeisimmät uutuudet — Pikler-kolmiot ja
              kiipeilytelineet, oppimistornit keittiöön, pehmopalikat ja
              motoriikkalelut, jotka on suunniteltu tukemaan lapsen luontaista
              uteliaisuutta. Kaikki tuotteet on valmistettu kestävästä koivuvanerista
              tai muista huolella valituista materiaaleista. Tuotekohtaiset turvallisuus- ja materiaalitiedot löydät jokaiselta tuotesivulta.
            </p>
            <p>
              Päivitämme uutuudet-sivun aina, kun mallistoon saapuu uusia
              tuotteita — kannattaa siis pistää sivu kirjanmerkkeihin. Jokaisen
              tuotteen sivulta löydät yksityiskohtaiset mitat, materiaalitiedot,
              ikäsuositukset ja toimitusarvion Suomeen.
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
}